import { getOrganizationById, getScopeByName } from "@utils/api-communication/RestApiCli";
import { selectUserOrgId } from "@utils/redux-utils/selectors/userSelectors";
import { setUserOnboarding } from "@actions/userActions";
import { fetchUserOnboarding } from "@utils/onboarding/fetchUserOnboarding";
import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchAdminConfigurations } from "@store/adminConfigurations/adminConfigurationsSlice";

interface AuthContextValue {
  scopes: string[];
  scopesLoaded: boolean;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    console.error("useAuthContext must be used in AuthProvider");
    throw new Error("useAuthContext must be used in AuthProvider");
  }
  return context;
};

export default function AuthProvider({ children }) {
  const [scopes, setScopes] = useState<string[]>([]);
  const [scopesLoaded, setScopesLoaded] = useState(false);
  const orgId = useSelector(selectUserOrgId);
  const dispatch = useDispatch();

  useEffect(() => {
    if (orgId) {
      dispatch(fetchAdminConfigurations());
    }
  }, [orgId, dispatch]);

  // Keeps the onboarding menu entry current for sessions restored from storage.
  useEffect(() => {
    if (!orgId) return;
    let active = true;
    fetchUserOnboarding()
      .then((onboarding) => {
        if (active) dispatch(setUserOnboarding(onboarding));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [orgId, dispatch]);

  useEffect(() => {
    let active = true;

    // Reset until the new lookup resolves so gated routes never read stale
    // permissions; the `active` guard drops late responses from a prior org
    // (or after unmount) that would otherwise repopulate scopes.
    setScopes([]);
    setScopesLoaded(false);

    if (!orgId) {
      setScopesLoaded(true);
      return;
    }

    const resolve = (list: string[]) => {
      if (!active) return;
      setScopes(list);
      setScopesLoaded(true);
    };

    getOrganizationById(
      orgId,
      (resp) => {
        if (!active) return;
        const scopeName = resp.status === 200 ? resp.data?.scopeName : null;
        if (!scopeName) {
          resolve([]);
          return;
        }
        getScopeByName(
          scopeName,
          (resp) =>
            resolve(resp.status === 200 && Array.isArray(resp.data?.list) ? resp.data.list : []),
          () => resolve([]),
        );
      },
      () => resolve([]),
    );

    return () => {
      active = false;
    };
  }, [orgId]);

  const value = useMemo(() => ({ scopes, scopesLoaded }), [scopes, scopesLoaded]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
